import { AccessDeniedError } from '@adornis/abac/errors/AccessDeniedError.js';
import { logger } from '@adornis/base/logging.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { requireUser } from '../authz/access.js';
import { Conversation } from '../conversation.js';
import { Exam } from '../exam.js';
import { Submission } from '../submission.js';

export const claimGuestConversationMutation = registerMutation({
  operationName: 'claimGuestConversation',
  type: () => Boolean,
  params: [
    { name: 'conversationId', type: () => String },
    { name: 'userId', type: () => String },
  ],
  resolve: (conversationId: string, _userId: string) => async () => {
    const normalizedConversationId = String(conversationId || '')
      .trim()
      .replace(/^"(.+)"$/, '$1')
      .replace(/^'(.+)'$/, '$1');

    if (!normalizedConversationId) {
      throw new Error('Conversation-ID ist leer.');
    }

    // Only a signed-in account can claim, and only what THIS session's guest owned: `userId` from the client is
    // ignored, and the guest id comes from the session (startup.ts `upgrade`), never from a parameter.
    const { userID: resolvedUserId, guestID } = await requireUser();
    if (!guestID) throw new AccessDeniedError('Access denied: no guest session to claim from', { reason: 'no guest' });

    logger.info({ normalizedConversationId, collectionName: Conversation._collectionName }, 'Backend search for chat in collection');

    const rawConversationCollection = await getRawCollection(Conversation._collectionName);
    const rawExamCollection = await getRawCollection(Exam._collectionName);

    const conversation = await rawConversationCollection.findOne({
      $or: [{ _id: normalizedConversationId }, { id: normalizedConversationId }],
      userID: guestID,
    } as any);

    if (!conversation) {
      logger.error(
        { normalizedConversationId, collectionName: Conversation._collectionName },
        'Conversation not found in collection',
      );
      throw new Error('Conversation nicht gefunden.');
    }

    await rawConversationCollection.updateOne({ _id: conversation._id }, { $set: { userID: resolvedUserId } });

    if (conversation.examId) {
      await rawExamCollection.updateOne(
        {
          _id: conversation.examId,
          $or: [{ userID: null }, { userID: { $exists: false } }, { userID: conversation.userID }],
        },
        { $set: { userID: resolvedUserId } },
      );
    }

    // Only what belongs to THIS conversation moves to the account; other guest data stays with the guest id
    // (invisible once signed in, see authz/subject.ts `ownerIds`). Files carry no back-reference, so the
    // guest's files are matched against the ids the conversation, its exam and its submissions mention.
    const rawSubmissionCollection = await getRawCollection(Submission._collectionName);
    const examDoc = conversation.examId ? await rawExamCollection.findOne({ _id: conversation.examId }) : null;
    const submissions = conversation.examId
      ? await rawSubmissionCollection.find({ examId: conversation.examId }).toArray()
      : [];
    if (conversation.examId) {
      await rawSubmissionCollection.updateMany(
        { examId: conversation.examId, userID: guestID },
        { $set: { userID: resolvedUserId } },
      );
    }

    const rawFileCollection = await getRawCollection(AdornisFile._collectionName);
    const referenced = JSON.stringify([conversation, examDoc, submissions]);
    const guestFileIds = (await rawFileCollection.find({ ownerUserID: guestID }, { projection: { _id: 1 } }).toArray())
      .map(file => String(file._id))
      .filter(fileId => referenced.includes(fileId));
    if (guestFileIds.length) {
      await rawFileCollection.updateMany(
        { _id: { $in: guestFileIds as any[] }, ownerUserID: guestID },
        { $set: { ownerUserID: resolvedUserId } },
      );
    }

    logger.info(`✅ Update in der rohen DB erfolgreich ausgeführt.`);
    return true;
  },
});
